// ============================================================================
// ui/shell/side-panel.js — left slide-out editing pane (reusable shell)
// ============================================================================
// Ports the Gym Designer's `.rd-side-panel` (dark): an absolutely-positioned
// overlay inside the canvas that slides in from the left, with three states —
// hidden / open (320px) / minimized (48px vertical rail; a bottom sheet on
// ≤480px, handled in CSS).
//
// Collapse/expand is a SINGLE chevron in the header (exactly like the GD): it
// points ◀ when open (click = collapse to the rail) and ▶ when minimized
// (click = expand). The icon is swapped via innerHTML — no CSS rotate — matching
// the GD 1:1. Clicking anywhere on the collapsed rail also re-opens it.
//
//   const panel = createSidePanel({ onMinimize, onOpen });
//   panel.setBody(stepEl).setTitle('Design').open();
//
// Glass law: the panel is frosted chrome; it overlays (never pushes) the clean
// canvas, and does NOT auto-close on outside click (the host drives open/close).
// ============================================================================

import { h, replaceChildren } from '../dom.js';

// Chevron points LEFT when open ("collapse me"); RIGHT when minimized ("expand
// me"). Same 24×24 polylines the GD swaps between.
const ICON_CHEVRON_LEFT =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 4 9 12 15 20"/></svg>';
const ICON_CHEVRON_RIGHT =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" ' +
  'stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 4 15 12 9 20"/></svg>';

/**
 * @param {object} [config]
 * @param {string} [config.title]        - initial title.
 * @param {()=>void} [config.onOpen]
 * @param {()=>void} [config.onMinimize]
 * @param {()=>void} [config.onClose]     - fired by the programmatic close() (no close button).
 * @param {boolean} [config.dragToggle]   - SHEET MODE: the header is a drag handle (up = expand,
 *   down = collapse) and tapping it does nothing. Off by default, which keeps the desktop rail's
 *   click-anywhere-to-reopen. See the block above the pointer handlers for why they're exclusive.
 * @returns {{
 *   el:HTMLElement, open:()=>object, minimize:()=>object, close:()=>object,
 *   setTitle:(t:string, opts?:{dir?:number})=>object, setBody:(node:Node|null)=>object,
 *   isOpen:()=>boolean
 * }}
 */
export function createSidePanel(config = {}) {
  const { title = '', onOpen, onMinimize, onClose, dragToggle = false } = config;

  const titleEl = h('span', { class: 'ds-side-panel-title' }, title);
  // A second, VERTICAL copy of the title for the collapsed rail. Keeping it as its
  // own element (rather than flipping the writing-mode of the one title) lets the two
  // CROSS-FADE on collapse/expand instead of snapping sideways (Ryan 2026-07-16).
  // Desktop-only (hidden on the mobile bottom-sheet, which stays horizontal).
  const titleRailEl = h('span', { class: 'ds-side-panel-title-rail', 'aria-hidden': 'true' }, title);
  const body = h('div', { class: 'ds-side-panel-body' });

  const chevronIcon = h('span', { 'aria-hidden': 'true', html: ICON_CHEVRON_LEFT });
  const collapseBtn = h(
    'button',
    {
      type: 'button',
      class: 'ds-side-panel-btn ds-side-panel-collapse wd-flat-glass wd-flat-glass--flush',
      'aria-label': 'Collapse panel',
      title: 'Collapse',
      onClick: (e) => {
        e.stopPropagation();
        if (el.classList.contains('is-minimized')) open();
        else minimize();
      },
    },
    chevronIcon,
  );

  // An optional host-supplied control in the header row, left of the chevron. The shell stays
  // step-agnostic — it offers a SLOT and never learns what goes in it (main.js puts the phone's
  // "Next" there). Empty by default and `:empty`-collapsed in CSS, so desktop is untouched.
  const actionSlot = h('div', { class: 'ds-side-panel-action' });
  const header = h('div', { class: 'ds-side-panel-header' }, titleEl, titleRailEl, actionSlot, collapseBtn);
  // Persistent footer (below the swappable body). It used to host the always-available "Save & share"
  // control (Fable §5.7); that moved to the canvas cluster 2026-07-25, so the footer is EMPTY unless
  // a caller sets one — and an empty one is a blank bar with a divider above it, which is what Ryan
  // saw. It is now created only on demand by setFooter(); the API is unchanged for any future caller.
  let footer = null;

  const el = h(
    'div',
    {
      class: 'ds-side-panel',
      role: 'region',
      'aria-label': 'Editing panel',
      // Clicking anywhere on the collapsed rail (except the chevron) re-opens it — DESKTOP ONLY.
      // In sheet mode the header is a drag handle instead, and a tap that also toggled would make
      // every aborted drag commit something (Ryan 2026-07-27: "No just tapping anywhere on the
      // header to get it to expand and/or collapse").
      onClick: () => {
        if (!dragToggle && el.classList.contains('is-minimized')) open();
      },
    },
    header,
    body,
    // footer is appended lazily by setFooter() — see above.
  );

  // ── SHEET MODE: the header is a DRAG HANDLE, and the sheet FOLLOWS THE FINGER ─────────────────
  // Ryan 2026-07-27: "if you grab it with your finger and try to slide it up, nothing happens."
  // Ryan 2026-07-28: "when someone clicks and drags the pane up or down, I want to see the pane move
  // with your finger."
  //
  // The first pass was threshold-commit — past 24px, run the normal open/minimise transition — on the
  // reasoning that the two states differ by `max-height` rather than a transform, so tracking a
  // finger means driving a LAYOUT property per frame. That is still true; it is just worth it. A
  // sheet that only reacts after you let go feels like a button you dragged, not a sheet.
  //
  // How it works, and the three things that make it behave:
  //   · The CSS transition is suppressed for the length of the gesture. Leaving it on would make
  //     every per-frame height a fresh 300ms ease toward a moving target — the exact lag that made
  //     the step-nav bar feel detached (entry 109). During a drag there is nothing to animate: the
  //     finger IS the animation.
  //   · The open height is MEASURED once at grab time rather than assumed. It is `min(content, cap)`
  //     and the cap is a CSS `min()` of two values this component deliberately knows nothing about,
  //     so it is read off the element itself by briefly forcing the open state with transitions off.
  //   · Release decides by WHERE IT ENDED, not which way it moved: past the midpoint it opens, below
  //     it collapses. A drag that reverses direction should land where the buyer left it, and a
  //     direction test would send it wherever the last few pixels happened to point.
  //
  // Everything that reads the sheet's height follows for free — main.js drives both the step-nav
  // bar's lift and the 3D's re-frame off a ResizeObserver on this element, so they track the finger
  // too rather than needing to know a drag is happening.
  /* Below this it is a tap, not a drag — leave the state alone.
       6 → 12 on 2026-08-09, with the swipe rewrite. At 6 this only decided whether the sheet
     FOLLOWED the thumb, and a stray few pixels cost nothing because releasing snapped back to the
     nearer state. It now decides an OUTCOME on the first pixel past it, and direction is the whole
     input — so a thumb rolling 7px downward while tapping a minimised sheet would have read as
     "swipe closed", no-op'd, and swallowed the tap that was meant to open it. 12px is still "begins
     to drag" and is comfortably past a tap's own wobble. */
  const DRAG_COMMIT_PX = 12;
  /** Set by `setDragLocked` — read by both gestures and by `open`/`minimize`'s aria. */
  let dragLocked = false;

  if (dragToggle) {
    let startY = null;
    let moved = false;
    /* TAPPING THE STEP NAME TOGGLES THE SHEET (Ryan 2026-07-28: "to get the menu sliding up and down,
       one can tap the name of the tab (middle), or grab anywhere on it and slide up/slide down").
         Resolved from the pointerdown TARGET in `finish`, not from a `click` listener on the title:
       the header takes pointer capture for the drag, so the click that follows is retargeted to the
       capture element and a title-scoped click handler would simply never fire. Doing it here also
       means the tap/drag split is decided once, by the same `moved` flag, instead of twice.

       ── AND THE TARGET IS THE WHOLE GAP BETWEEN THE PILLS, NOT THE WORDS (Ryan 2026-08-08) ────────
       *"just make the clickable area as wide as the space between the two pills — whether they're
       both there or not?"* — which is what `downOnTitle` now means. Two things make it a one-line
       change rather than a layout one:
         · the pills and the chevron already `return` from this handler a few lines below, so
           "the pointer went down on the header and was not sent home" IS "between the pills";
         · a hidden pill is `display: none`, so its side of the gap simply extends to the header's
           edge — the "whether they're both there or not" half, for free.
       The NAME is still the only thing that LOOKS tappable, and that is the right split: a hit area
       larger than its affordance is the standard phone remedy for a 60px target on a 44px thumb.

       ⚠ THIS DOES REOPEN A DECISION, and not by accident. 2026-07-27 was *"No just tapping anywhere
       on the header to get it to expand and/or collapse"*, on the grounds that the header is a drag
       surface and every aborted drag would commit something. That objection is answered by the
       `moved` flag and `DRAG_COMMIT_PX` — the tap/drag split those introduced is what makes a wide
       tap zone safe now and did not exist then — and Ryan has asked for the wider area since. Kept
       out of the zone: the pills, the chevron, and anything else a host puts in the action slot. */
    let downOnTitle = false;
    const toggle = () => (el.classList.contains('is-minimized') ? open() : minimize());
    /** Both gestures ask this first. See `setDragLocked`. */
    const gestureBlocked = () => dragLocked;

    // The name is a real control now, so it has to answer to a keyboard and announce itself as one.
    // Only in sheet mode — on desktop the title is a label and the chevron beside it is the control.
    titleEl.setAttribute('role', 'button');
    titleEl.setAttribute('tabindex', '0');
    titleEl.addEventListener('keydown', (e) => {
      if (gestureBlocked()) return;
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      e.preventDefault();          // Space would otherwise scroll the page behind the sheet
      toggle();
    });

    header.addEventListener('pointerdown', (e) => {
      // LOCKED: the sheet is not collapsible in this state, so the gesture never starts — which is
      // also why neither `pointermove` nor `finish` needs its own guard: both bail on `startY == null`.
      if (gestureBlocked()) return;
      // The chevron and the header action are their own controls and own their clicks; a drag
      // starting on either is not a sheet drag. Without this, pressing "Next" would begin a drag and
      // a few px of thumb travel would collapse the sheet instead of advancing the step.
      if (collapseBtn.contains(e.target) || actionSlot.contains(e.target)) return;
      startY = e.clientY;
      moved = false;
      // Everything the pills and the chevron own was sent home by the guard above, so what is left
      // is the gap between them — the whole of it, and the whole header height. See the note above.
      downOnTitle = true;
      // A step-change height ease still running would fight the drag: it drives `height` while the
      // drag drives `max-height`, and the smaller of the two wins, so the sheet would refuse to grow
      // past whatever the ease had reached. The finger outranks it.
      releaseHeight();
      try { header.setPointerCapture(e.pointerId); } catch { /* not capturable — the drag still works */ }
    });

    /* ── A SWIPE COMMITS; IT DOES NOT DRIVE (Ryan 2026-08-09) ──────────────────────────────────
     * *"as soon as someone puts their finger and begins to drag down to manually swipe it shut or
     * open, should it behave just like a click and automatically fully close or open as if you had
     * clicked it? Since clicking produces no flashes, this should be a sure fire way to ensure no
     * flashes at all."*
     *
     * 🔑 IT IS, AND IT REMOVES THE CAUSE RATHER THAN THE COST. Everything that flashed came from the
     * finger driving `max-height` frame by frame: each write relaid out the body, exposed scroller
     * regions that had never been rastered, and handed the compositor tiles it could not fill in
     * time. Freezing the body's height and standing the thumbnail drain down made that cheaper. This
     * makes it not happen — there is no per-frame layout left to be expensive, and the motion is the
     * same CSS transition a tap has always used, which is the one path measured clean all along.
     *
     * What is given up is 1:1 tracking: the sheet no longer follows the thumb, and you can no longer
     * change your mind halfway and release back to where you started. DIRECTION decides — down
     * closes, up opens — from the first committed pixel.
     *   ⚠ `DRAG_COMMIT_PX` is what keeps that safe and is now load-bearing rather than a nicety. It
     * was already the line between a tap and a drag; it is now also the line between "brushed the
     * header" and "shut the sheet", so a stray few pixels while reaching for a pill must not commit.
     *
     * The gesture ENDS here, at the commit — `startY` is cleared and the capture released — so the
     * rest of the finger's travel is inert and `finish` has nothing left to do. That is what stops a
     * long swipe from queueing a second decision when the thumb lifts. */
    header.addEventListener('pointermove', (e) => {
      if (startY == null) return;
      const dy = e.clientY - startY;
      if (Math.abs(dy) < DRAG_COMMIT_PX) return;   // still a tap; don't disturb anything yet
      moved = true;
      startY = null;                               // spent — see the note above
      try { header.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      if (dy > 0) minimize(); else open();
    });

    const finish = () => {
      if (startY == null) return;   // either a committed swipe, or no gesture at all
      startY = null;
      // A TAP anywhere in the gap between the pills toggles the sheet; the pills and the chevron
      // never reach here (the pointerdown guard sends them home). See `downOnTitle`.
      if (!moved && downOnTitle) toggle();
    };
    header.addEventListener('pointerup', finish);
    /* Nothing to restore any more: a cancelled gesture either never committed (so the sheet was
       never touched) or already committed (so it is mid-transition and owns itself). */
    header.addEventListener('pointercancel', () => { startY = null; });
  }

  /** Put a node in the header's action slot (or null to clear it). */
  function setHeaderAction(node) {
    actionSlot.replaceChildren();
    if (node) actionSlot.appendChild(node);
    return api;
  }

  const api = { el, open, minimize, close, setTitle, setBody, setFooter, setHeaderAction, easeContentChange, isOpen, setWide, setDragLocked, scrollBodyTop, setBodyBusy, freezeBodyWidth, releaseBodyWidth };

  /**
   * Pin the body to the width it has RIGHT NOW, so its contents cannot re-lay-out while the panel
   * animates to a DIFFERENT width.
   *
   * The narrowing half of this was always handled — `min-width: 320px` in the CSS, so the body clips
   * behind the panel's `overflow: hidden` on collapse rather than squashing (2026-07-16). The
   * GROWING half had nothing: the body is full-width, so it tracked the pane out to the whole frame
   * and every card, grid and heading inside re-flowed on the journey (Ryan 2026-08-07: *"while it's
   * growing and things are fading, they're also repositioning... we need them to just fade away"*).
   * Fading and re-flowing are two different things and only one of them was ever asked for.
   *
   * ⚠ THE FOOTER IS PINNED TOO (Ryan 2026-08-07: *"can you include the footer buttons in this too?
   * They do some jumping/stretching when you click the button to first leave the CD tab"*). It is a
   * SIBLING of the body, not a child, so freezing the body left it stretching on its own — and it
   * stretches harder than anything in the body does, because `.wd-panenav` centres itself in a
   * 760px reading column, so growing past 760 moves the buttons inward as well as apart. Same
   * treatment, same two moments; the only reason it is a second line rather than a loop is that
   * `footer` may not exist yet.
   *
   * MEASURE BEFORE THE WIDTH CLASS CHANGES, or this pins the destination instead of the origin.
   */
  function freezeBodyWidth() {
    const w = body.getBoundingClientRect().width;
    if (w > 0) body.style.width = `${w}px`;
    if (footer) {
      const fw = footer.getBoundingClientRect().width;
      if (fw > 0) footer.style.width = `${fw}px`;
    }
    return api;
  }
  /** Hand the widths back to the layout - once the new content is in (main.js `fillPane`). */
  function releaseBodyWidth() {
    body.style.width = '';
    if (footer) footer.style.width = '';
    return api;
  }

  /**
   * Put the body back at the top.
   *
   * `setBody` already does this on every step change, so this is for the host's own moves WITHIN a
   * step — today the pane nav walking the weight ladder, where the tab does not change but its
   * whole content does (Ryan 2026-08-06: *"scrolling the pane to top on each move"*). Exposed as a
   * method because `body` is this component's own node and no caller should be reaching into it.
   */
  function scrollBodyTop() {
    body.scrollTop = 0;
    return api;
  }

  /**
   * FULL-WIDTH MODE (Ryan 2026-08-05: *"when they go to this step, on desktop, the right-side pane
   * can slide out to cover the entire window — giving the person plenty of room to read and absorb
   * all the questions in the form, and more easily navigate through the questions and answers"*).
   *
   * A pure width change, so it rides the `width` transition the collapse choreography already
   * declares — the pane grows into the stage rather than a new surface appearing over it.
   *
   * ⛔ IT MUST NEVER BE A ROOM WITH NO DOOR. A surface that covers the whole frame is exactly the
   * shape the parade got wrong (memory: *never enter a screen whose exit is conditional*), so the
   * exit is guarded and does not depend on the form's state: the step tabs live OUTSIDE the element
   * this panel is positioned inside (`.wd-frame` > `preview.el`), so no width can reach them — one
   * click on any other step both leaves the step and un-widens the pane.
   *
   * ⚠ THIS USED TO BE GUARDED TWICE. The collapse chevron was the second exit; Ryan removed it here
   * on 2026-08-07 (*"no need for this arrow… collapsing just reveals your 3D model that you can't
   * edit from this screen anyway"* — `.ds-side-panel.is-wide .ds-side-panel-collapse` in shell.css).
   * That is safe, but it spends the redundancy: the tabs are now the ONLY way out of full width.
   * So the claim above is load-bearing and was re-verified when the chevron went, not assumed —
   * `.ds-step-tabs` is a SIBLING of `.ds-canvas`, this pane is a CHILD of it, and `.ds-canvas` is
   * `overflow: hidden`, so `width: 100%` is 100% of the canvas and cannot reach the tabs at any
   * viewport. ⛔ If the pane is ever re-parented, or the tabs move inside `.ds-canvas`, the door is
   * gone and the chevron has to come back with it.
   */
  /**
   * Lock the sheet OPEN: no swipe, no tap-to-collapse, no grab line.
   *
   * ⛔ THE ONE EXIT MUST BE SOMETHING ELSE, AND THE HOST OWNS THAT. Memory
   * `never-enter-a-screen-whose-exit-is-conditional`: this removes a door, so whoever locks it is
   * asserting that at least one unconditional door remains. The Weight Designer's quote step locks
   * with two — the back pill, present on every step after the first, and the body-appended hamburger
   * (Ryan 2026-08-09: *"once it's open, you can only go back by clicking the 'customize' button… or
   * by clicking the hamburger and closing the app"*).
   *
   * ⚠ THE GRAB LINE GOES WITH THE GESTURE, in CSS off `is-drag-locked`. It was added on 2026-08-08
   * at Ryan's ask precisely because the sheet COULD be dragged down; a handle that no longer does
   * anything is worse than one that was never there. One state, one affordance.
   *
   * ⚠ AND THE TITLE STOPS BEING A CONTROL — `role`, `tabindex` and `aria-expanded` come off, not just
   * the listeners. A screen reader announcing a collapsed/expanded button that cannot be operated is
   * the same lie the grab line would be telling, in the other channel.
   *
   * Sheet mode only: the desktop pane has no gesture, and its chevron is a different exit entirely.
   */
  function setDragLocked(on) {
    if (!dragToggle) return api;
    dragLocked = !!on;
    el.classList.toggle('is-drag-locked', dragLocked);
    if (dragLocked) {
      titleEl.removeAttribute('role');
      titleEl.removeAttribute('tabindex');
      titleEl.removeAttribute('aria-expanded');
    } else {
      titleEl.setAttribute('role', 'button');
      titleEl.setAttribute('tabindex', '0');
      titleEl.setAttribute('aria-expanded', String(el.classList.contains('is-open')));
    }
    return api;
  }

  function setWide(on) {
    el.classList.toggle('is-wide', !!on);
    return api;
  }

  function open() {
    // Drop any height the close pinned (see `minimize`), or a re-open is clamped to the size the
    // sheet had on its way out — which is wrong the moment the new step is taller.
    releaseHeight();
    el.classList.remove('is-minimized');
    el.classList.add('is-open');
    chevronIcon.innerHTML = ICON_CHEVRON_LEFT;
    collapseBtn.setAttribute('aria-label', 'Collapse panel');
    collapseBtn.title = 'Collapse';
    if (dragToggle && !dragLocked) titleEl.setAttribute('aria-expanded', 'true');
    if (typeof onOpen === 'function') onOpen();
    return api;
  }
  function minimize() {
    /* ── A CLOSE ANIMATES FROM THE HEIGHT YOU WERE LOOKING AT (Ryan 2026-08-08) ──────────────────
     * *"when I go to close it on mobile, instead of it being smooth all the way down, it jumps down
     * part way, then smoothly contracts the rest of the way"* — and, decisively, *"it is ONLY
     * happening on the Logo sub-tab."*
     *
     * 🔑 MEASURED, after the obvious answer was refuted. `tools/probe-close-trace.mjs` records the
     * close with Colors as the control: at the instant `is-minimized` lands, Colors is still 460px
     * and eases smoothly to 48, while Logo is ALREADY at 286.7 — its body having gone 411 → 238 in
     * the same breath. The 173px is gone before the transition starts, which is exactly a jump
     * followed by a smooth contraction.
     *   ⛔ AND THERE IS NO MUTATION BEHIND IT. Watching `style`, `class`, `hidden` and `childList`
     * across the whole subtree shows nothing between the tap and the class change, so this is a
     * layout-only shrink — the Logo section is built from BORROWED nodes (`logoLoan.sizeBlock` /
     * `nudgeBlock`, design-panel.js `logoSection`) and it is the one section that legitimately loses
     * height as it goes away.
     *
     * So the content change is not the bug; it being VISIBLE is. `easeAround` already enforces this
     * invariant for a step swap — pin the height first, so no layout in the window is ever the new
     * one — and a close simply never had it. The pin is a FLOOR the collapsing `max-height` walks
     * down past (the used height is `min(height, max-height)`), so it changes nothing about the
     * animation itself; it only stops the box tracking content that is on its way out.
     *
     * ⚠ SHEET MODE ONLY. On desktop `minimize()` collapses to a 48px vertical rail by WIDTH, and
     * pinning a height there would fight the rail's own layout for no benefit — nothing on that path
     * reported a jump.
     */
    if (dragToggle && el.classList.contains('is-open')) {
      const from = el.getBoundingClientRect().height;
      if (from > 0) {
        if (heightEaseTimer) { clearTimeout(heightEaseTimer); heightEaseTimer = 0; }
        el.style.height = `${from}px`;
        /* Released on a timer for the same reason `easeFrom` uses one: `transitionend` does not fire
           for a transition that is interrupted or never starts, and a stuck inline height would leave
           the collapsed bar 460px tall for the rest of the session. `open()` clears it too, so a
           re-open is never clamped by the height the close was pinned at. */
        heightEaseTimer = setTimeout(() => { heightEaseTimer = 0; el.style.height = ''; }, SHEET_CLOSE_MS + 80);
      }
    }
    el.classList.remove('is-open');
    el.classList.add('is-minimized');
    chevronIcon.innerHTML = ICON_CHEVRON_RIGHT;
    collapseBtn.setAttribute('aria-label', 'Expand panel');
    collapseBtn.title = 'Expand';
    if (dragToggle && !dragLocked) titleEl.setAttribute('aria-expanded', 'false');
    if (typeof onMinimize === 'function') onMinimize();
    return api;
  }
  function close() {
    releaseHeight();   // never leave a close-pinned height on a panel that is going away entirely
    el.classList.remove('is-open', 'is-minimized');
    if (typeof onClose === 'function') onClose();
    return api;
  }
  // ── The title SWIPES between steps ────────────────────────────────────────────────────────────
  // Ryan 2026-07-28: "each time the 'next' button is clicked, have the center text swipe left and
  // fade while the new header text fades in and slides in from the right. Do the opposite when we
  // click the PREV button."
  //
  // `dir` is +1 forward / −1 back / 0 no animation, and the host supplies it — the shell stays
  // step-agnostic and never learns what a step is, exactly as it does for the action slot.
  //
  // Web Animations rather than CSS classes for one reason: a second step change mid-swipe has to be
  // able to CANCEL the first cleanly. With classes that means tracking transitionend, removing and
  // reflowing to restart, and a stuck class leaves the title parked off-centre forever. Here the
  // in-flight animation is an object that can simply be dropped.
  //   Travel is short (18px) on purpose. The title lives in the middle grid column with a button
  // either side and nothing clips the header, so a long slide would visibly cross over PREV/NEXT;
  // at 18px with the fade running alongside, the text is transparent well before it reaches one.
  const TITLE_SLIDE_PX = 18;
  const reducedMotion =
    typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  let titleAnim = null;
  let titleAnimPhase = null; // 'out' | 'in' | null

  /** IN: the new label arrives FROM the travel direction, and slower than it left, so it settles
   *  rather than snapping — the same fast-out/slow-in asymmetry the sheet's own transition uses. */
  function enterTitle(t, dir) {
    titleEl.textContent = t;
    const enter = titleEl.animate(
      { transform: [`translateX(${dir * TITLE_SLIDE_PX}px)`, 'translateX(0)'], opacity: [0, 1] },
      { duration: 190, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
    );
    titleAnim = enter;
    titleAnimPhase = 'in';
    enter.finished
      .then(() => { if (titleAnim === enter) { titleAnim = null; titleAnimPhase = null; } })
      .catch(() => {});
  }

  function setTitle(t, opts = {}) {
    titleRailEl.textContent = t;
    const dir = Number(opts && opts.dir) || 0;
    // If the PREVIOUS swipe was still on its way OUT, that label has already left — replaying its
    // exit would snap it back to centre at full opacity and send it away a second time, which is the
    // flicker you get tapping NEXT twice quickly. Pick up at the entry instead.
    const midExit = titleAnimPhase === 'out';
    if (titleAnim) { titleAnim.cancel(); titleAnim = null; titleAnimPhase = null; }
    if (!dir || typeof titleEl.animate !== 'function' || (reducedMotion && reducedMotion.matches)) {
      titleEl.textContent = t;
      return api;
    }
    if (midExit) { enterTitle(t, dir); return api; }
    // OUT: the old label leaves AGAINST the travel — forward sends it left, back sends it right.
    const out = titleEl.animate(
      { transform: ['translateX(0)', `translateX(${-dir * TITLE_SLIDE_PX}px)`], opacity: [1, 0] },
      { duration: 130, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' },
    );
    titleAnim = out;
    titleAnimPhase = 'out';
    out.finished
      .then(() => {
        if (titleAnim !== out) return; // superseded — the newer call owns the title now
        out.cancel(); // drop the `forwards` fill, or the entry would start from the parked offset
        enterTitle(t, dir);
      })
      .catch(() => { /* cancelled by a newer setTitle — it has already taken over */ });
    return api;
  }
  // ── The sheet EASES to the new step's height ──────────────────────────────────────────────────
  // Ryan 2026-07-28: "if the slide-up menu is up, and someone clicks Next or Prev to go to a menu
  // which has a different default menu height… instead of having the menu snap to a new height, have
  // it smoothly slide up or slide down to the new height."
  //
  // Nothing was animating it because nothing COULD: the sheet is `height: auto` under a `max-height`
  // ceiling, so its height is whatever the step's content needs, and a content-driven auto height has
  // no start value to transition from. The class transition covers `transform` and `max-height`, and
  // neither of those is what changes here.
  //
  // So the two heights are measured and driven explicitly, through an INLINE `height` against a CSS
  // transition that now lists `height` (shell.css).
  //
  // ⚠ THE HEIGHT IS PINNED BEFORE THE CONTENT CHANGES, and that is the whole trick (Ryan 2026-07-28:
  // "the 3D weight model will flash and freak out quickly on click"). The first version used Web
  // Animations, which reads better — no cleanup, cancellable — but `el.animate()` does not resolve its
  // start time until the NEXT frame tick, so the frame in which the content swapped was free to lay
  // out and paint at the new height before the animation had any say. One frame at full height is
  // invisible as a height, but main.js re-frames the 3D off a ResizeObserver on this element, so the
  // camera took that frame as a real measurement and refitted to it, then refitted back as the
  // animation ran from the old height. That is the flash: the sheet was fine, the MODEL jumped.
  //   Pinning `height` to the current value first means no layout in that window is ever the new one.
  //
  // ⚠ The target measurement is deferred one frame, also on purpose. main.js calls `steps[n].refresh()`
  // right AFTER setBody, synchronously, and that can change the content's height — measuring here
  // would ease to a stale target and jump on release. The measure/restore inside that frame is
  // synchronous and transition-free (the same forced-then-restored read the drag used to take), so the
  // ResizeObserver — which reports once per frame, at delivery — never sees the transient either.
  const HEIGHT_EASE_MS = 300;
  /* The sheet's own open/close, which is `max-height 0.55s` in shell.css. ⚠ IT HAS TO AGREE WITH THAT
     DECLARATION: `minimize()` holds an inline height for this long so a section that shrinks on its
     way out cannot be seen doing it, and releasing early would show exactly the jump it exists to
     hide. If the CSS duration is ever retuned, retune this with it — they are one decision. */
  /* ⚠ 550 → 800 → 550 (2026-08-08, then back 2026-08-09). It went up for the quote step, which opened
     full-screen on an 0.8s `max-height` of its own; the rebuild dropped that override — the rise
     travels ~384px, which is the ordinary pane's own distance, so it rides the ordinary 0.55s and
     there is no longer a longer close for this to cover. If any host ever gives the sheet a slower
     `max-height` again, this moves with it: releasing the pin early drops the hold before the close
     lands and lets a shrinking section show through, which is the jump it exists to hide. */
  const SHEET_CLOSE_MS = 550;
  let heightEaseTimer = 0;
  /** Hand the height back to CSS: `auto`, which is what it already renders at once the ease lands. */
  function releaseHeight() {
    if (heightEaseTimer) { clearTimeout(heightEaseTimer); heightEaseTimer = 0; }
    el.style.height = '';
  }
  /**
   * The body's entrance, and the "hold still, new content is coming" state it can be parked in.
   *
   * Ryan 2026-08-07: *"when you click on the Add Logo tab, the contents of the side panel smoothly
   * fade in / slide up. It looks clean. This doesn't happen on the other tabs."* It didn't, because
   * that motion was never the PANEL's — it belongs to Add Logo's own cards (`.wd-reveal`,
   * `.wd-resume-card` and friends each carry `wtfRise`). Every other tab simply appeared. Moving it
   * onto the body gives every tab the same arrival for free, and lets the slow ones hide their work.
   *
   * ⚠ RESTARTED BY REMOVE → REFLOW → ADD. Re-inserting a node restarts a CSS animation, but the body
   * element itself is NOT re-inserted — only its children are — so the class has to be cycled by
   * hand or a second tab change inside one animation would not replay. (The mirror of the trap
   * `.wd-reveal-sizeblock` documents: there a node WAS re-inserted, and a standing rule replayed the
   * rise on every visit.)
   */
  const BODY_ENTER_MS = 260;
  let bodyEnterTimer = 0;
  function playBodyEnter() {
    if (reducedMotion && reducedMotion.matches) return;
    clearTimeout(bodyEnterTimer);
    body.classList.remove('is-entering');
    void body.offsetWidth;                       // commit the removal, so the add is a fresh start
    body.classList.add('is-entering');
    bodyEnterTimer = setTimeout(() => body.classList.remove('is-entering'), BODY_ENTER_MS + 60);
  }

  /**
   * Park the body OUT (faded, nudged down) while something slow rebuilds it, then let it back in.
   *
   * Ryan, on hopping between weight models: *"the slowest part of the process is the SVGs loading,
   * the new size buttons loading… perhaps everything slides out on the slide-out pane, then slides
   * back in once the new model has loaded."* Exactly that — the rebuild still costs what it costs,
   * but it happens behind a faded panel instead of in front of the buyer, so a stutter reads as a
   * transition rather than as lag.
   *
   * `busy(false)` routes through the entrance above, so coming back is the same motion arriving on a
   * tab is — one vocabulary for "new content just landed", however it got here.
   */
  function setBodyBusy(on) {
    if (reducedMotion && reducedMotion.matches) { body.classList.remove('is-busy'); return api; }
    if (on) { clearTimeout(bodyEnterTimer); body.classList.remove('is-entering'); body.classList.add('is-busy'); }
    else if (body.classList.contains('is-busy')) { body.classList.remove('is-busy'); playBodyEnter(); }
    return api;
  }

  /**
   * Put a step's content in the sheet, easing the height across the swap.
   *
   * @param {Node} node the new body content
   * @param {{ease?: boolean}} [opts] `ease: false` hands the height change to CSS instead
   *
   * ⛔ `ease: false` IS FOR A SWAP WHOSE HEIGHT IS DRIVEN BY THE CAP, NOT BY THE CONTENT (2026-08-09
   * — the Weight Designer's full-screen quote step). The ease and the cap cannot both drive: the used
   * height is `min(height, max-height)`, so an inline pin is a CEILING as well as a floor. Pin at the
   * old height while `max-height` is climbing and the sheet is held at the old height until the pin
   * is released, then jumps to wherever the cap has got to; pin at the old height while `max-height`
   * is falling and the cap wins immediately and the ease animates nothing at all. That second case is
   * exactly the reported "it snaps down instead of sliding" on the way out of the quote step.
   *   So when the host knows the cap is the thing moving, it says so, and this stands down. The
   * content swaps in one frame behind `playBodyEnter`'s fade — which is the right reading anyway:
   * nothing about the height here is a consequence of what the new content needs.
   */
  function setBody(node, opts) {
    // Only when the sheet is UP. Minimised, the height is pinned to the header by `max-height` and
    // the body is not visible, so there is nothing to ease.
    const easeHeight =
      !(opts && opts.ease === false)
      && dragToggle
      && el.classList.contains('is-open')
      && !(reducedMotion && reducedMotion.matches);
    if (!easeHeight) {
      releaseHeight();
      replaceChildren(body, node);
      body.scrollTop = 0;
      playBodyEnter();
      return api;
    }
    easeAround(() => { replaceChildren(body, node); body.scrollTop = 0; playBodyEnter(); });
    return api;
  }

  /**
   * Run `mutate` — anything that changes what the sheet contains — and EASE the height across it
   * instead of letting it jump. The measure/pin/animate dance is identical whoever is mutating, so
   * it lives here once and `setBody` is simply its first caller.
   *
   * Exposed as `easeContentChange` because the host has its own reasons to change what is in the
   * sheet: typing mode hides all but the field being typed into (Ryan 2026-07-28: "can we have the
   * height of the entire slide-up window smooth and slidey up and down when you click in and out of
   * the text fields"). That is a class flip on an element this component does not own, so it cannot
   * be detected from in here — the host hands it over as a callback and gets the easing for free.
   */
  function easeAround(mutate) {
    const from = el.getBoundingClientRect().height;
    // Pin FIRST — before the mutation can produce a layout at its own height.
    el.style.transition = 'none';
    el.style.height = `${from}px`;
    void el.offsetHeight;

    mutate();

    /* ── AND IT SAYS WHEN IT HAS SETTLED (Ryan 2026-08-08) ────────────────────────────────────────
     * The host has work it must NOT run inside this animation — the design panel's layout carousel
     * is sixteen synchronous SVG builds, and draining them under a moving sheet is the flashing Ryan
     * reported on the Logo → Layout sub-tab. It already has the machinery to hold that work
     * (`setThumbsBusy`); what it lacked was a signal for how long.
     *   A PROMISE rather than a duration, because the duration is not knowable from outside: the
     * chase below can re-aim up to `MAX_CHASES` times, so "300ms" would release the host mid-flight
     * on exactly the content-heavy swaps this exists for.
     *   ⚠ IT ALWAYS RESOLVES. `releaseHeight` is also called from OUTSIDE this chain — a drag's
     * pointerdown clears the pin mid-ease — and a promise left pending would leave the host's work
     * suppressed for the rest of the session, i.e. a permanently blank carousel. The cap is the
     * belt: whatever happens to the animation, the host is released. */
    let settled;
    const done = new Promise((r) => { settled = r; });
    let fired = false;
    const finish = () => { if (fired) return; fired = true; settled(); };
    setTimeout(finish, (HEIGHT_EASE_MS + 60) * (MAX_CHASES + 1) + 120);
    requestAnimationFrame(() => easeFrom(from, 0, finish));
    return done;
  }

  /* How many times the ease may re-aim at a target that moved under it. Two covers a child whose own
     transition outlasts ours; it is a bound, not a budget, and hitting it releases rather than
     snapping again. */
  const MAX_CHASES = 2;

  /**
   * The measure/animate half of `easeAround`, split out so it can RE-AIM.
   *
   * ⛔ THE TARGET IS NOT A CONSTANT, AND ASSUMING IT WAS COST A VISIBLE SNAP (Ryan 2026-08-08). The
   * note above `HEIGHT_EASE_MS` already records one version of this — `refresh()` runs after
   * `setBody`, so the measurement is deferred a frame to let it land. That fixed the target moving
   * BEFORE the ease. This is the target moving DURING it: the Add Logo tab's showcase chip dismisses
   * with its OWN height transition, so the natural height we measured at frame one was still 58px
   * taller than the natural height 300ms later. The ease ran perfectly to a number that had expired,
   * and `releaseHeight` then dropped the sheet the difference in one frame — the only un-eased jump
   * left in the upload choreography, and measurable as such (tools/probe-sheet-rise.mjs).
   *
   * So on settling, re-measure. If the content has moved on, ease again from where we are instead of
   * releasing into a snap. Converges because the thing being chased is itself a finite animation,
   * and is bounded anyway by `MAX_CHASES`.
   *
   * ⚠ A CHASE IS NOT A POLL. It runs only when the settle finds a real discrepancy, so a static
   * sheet does exactly one ease and stops — the common case pays nothing for this.
   */
  function easeFrom(from, chase, finish) {
    // Read the natural height with the new content, then put the pin straight back. Nothing paints
    // and nothing observes in between: both happen inside this one callback.
    el.style.height = '';
    const to = el.getBoundingClientRect().height;
    el.style.height = `${from}px`;
    void el.offsetHeight;
    el.style.transition = '';                 // back to the class's list, which includes `height`
    if (!(from > 0) || !(to > 0) || Math.abs(to - from) < 1) { releaseHeight(); finish(); return; }
    el.style.height = `${to}px`;              // ← the transition starts here
    if (heightEaseTimer) clearTimeout(heightEaseTimer);
    // Released on a timer rather than `transitionend`: that event does not fire for a transition
    // that gets interrupted or never starts, and a stuck inline height would freeze the sheet at
    // one step's size for the rest of the session. The drag clears it too, on pointerdown.
    heightEaseTimer = setTimeout(() => {
      heightEaseTimer = 0;
      if (chase >= MAX_CHASES) { releaseHeight(); finish(); return; }
      // Same measure-and-restore as above, and for the same reason: transitions are off for the
      // length of it, so no frame is ever composited showing the unpinned height.
      el.style.transition = 'none';
      el.style.height = '';
      const atRest = el.getBoundingClientRect().height;
      if (Math.abs(atRest - to) < 1) { el.style.transition = ''; releaseHeight(); finish(); return; }
      el.style.height = `${to}px`;
      void el.offsetHeight;
      easeFrom(to, chase + 1, finish);
    }, HEIGHT_EASE_MS + 60);
  }

  /**
   * Host-facing wrapper: eases only in sheet mode and only while open, else just runs the change.
   *
   * ⚠ RETURNS A PROMISE THAT RESOLVES WHEN THE HEIGHT HAS SETTLED (2026-08-08) — see `easeAround`.
   * The paths that do NOT ease return an already-resolved one rather than `api`, so a caller can
   * always `.then()` it and gets "nothing is animating, go ahead" immediately on desktop, on a
   * minimised sheet and under reduced motion. Nothing chained the old `api` return.
   */
  function easeContentChange(mutate) {
    if (typeof mutate !== 'function') return Promise.resolve();
    const can = dragToggle && el.classList.contains('is-open') && !(reducedMotion && reducedMotion.matches);
    if (!can) { mutate(); return Promise.resolve(); }
    return easeAround(mutate);
  }
  function setFooter(node) {
    if (!footer) {
      footer = h('div', { class: 'ds-side-panel-footer' });
      el.appendChild(footer);
    }
    replaceChildren(footer, node);
    return api;
  }
  function isOpen() {
    return el.classList.contains('is-open');
  }

  return api;
}
